import { useEffect } from 'react';
import { useMultiLocationUtils } from '@frontend/payments-hooks';
import {
  PaymentsTerminalController,
  ReaderWithLocationID,
  StoredReader,
  TerminalReader,
} from '@frontend/payments-terminal-controller';
import { useAppScopeStore } from '@frontend/scope';
import { useModalControl } from '@frontend/design-system';

export type UseTerminalSelectionData = {
  locationId?: string;
  tempReader: TerminalReader | null;
  selectedReader: StoredReader | undefined;
  readers: TerminalReader[];
  discoverReaders?: (() => Promise<ReaderWithLocationID[]>) | undefined;
  onSelectReader: (reader: StoredReader) => void;
  onSelectTempReader: (reader: TerminalReader) => void;
};
export const useTerminalSelectionData = ({
  locationId,
  tempReader,
  readers,
  selectedReader,
  discoverReaders,
  onSelectReader,
  onSelectTempReader,
}: UseTerminalSelectionData) => {
  const { accessibleLocationIds: allLocations } = useAppScopeStore();
  const { getLocationName } = useMultiLocationUtils();

  const { modalProps, triggerProps } = useModalControl();

  const handleReaderSelect = async (reader: TerminalReader) => {
    try {
      if (!locationId || !reader) return;
      const selectedReaderTemp = readers?.find((terminal) => terminal.id === reader.id);

      if (selectedReaderTemp?.action?.status === 'in_progress') {
        onSelectTempReader(reader);
        triggerProps.onClick();
      } else {
        const selected = PaymentsTerminalController.createStoredReader(locationId, reader);
        onSelectReader(selected);
      }
    } catch (error) {
      console.error('error in connecting to the terminal', error);
    }
  };

  const onConfirm = async () => {
    try {
      if (!locationId || !tempReader) return;
      const selectedReaderTemp = readers?.find((terminal) => terminal.id === tempReader.id);
      await PaymentsTerminalController.cancelTerminalAction({
        readerId: selectedReaderTemp?.id || '',
        paymentIntentId: selectedReaderTemp?.action?.ProcessPaymentIntent?.id || '',
        locationId,
      });

      const selected = PaymentsTerminalController.createStoredReader(locationId, tempReader);
      onSelectReader(selected);
    } catch (error) {
      console.error('error canceling in progress payment', error);
    }
  };

  const showLocationChip = !!locationId && allLocations.length > 0;

  const locationName = getLocationName(locationId);

  useEffect(() => {
    /**
     * TODO
     * LEGACY TERMINALS: Remove once wetag is done
     */
    if (!!discoverReaders) {
      /**
       * LEGACY: Remove this when the v2 terminals are through.
       * Discover readers is only called for v2. If it is called, look to the other useEffect()
       * that doesn't have a `readers` dependency so that we don't start an infinite feedback loop.
       */
      return;
    }

    const maybeSelectStoredReader = async () => {
      if (!locationId) return;

      const storedReader = PaymentsTerminalController.getStoredReader(locationId);
      /* by checking for the selectedReader, we are automatically redirecting the user to the
      collection flow only for the first time until the modal is closed and opened again
      */
      if (storedReader && !selectedReader) {
        const storedReaderIsOnline = readers.find(
          (reader) => reader.id === storedReader.readerId && reader.status === 'online'
        );
        if (!storedReaderIsOnline) return;

        const storedReaderIsInUse = storedReaderIsOnline?.action?.status === 'in_progress';

        if (storedReaderIsInUse) return;

        onSelectReader(storedReader);
      }
    };

    maybeSelectStoredReader();
  }, [readers, locationId, selectedReader, onSelectReader]);

  useEffect(() => {
    /**
     * TODO
     * V2 terminals. Update once legacy block is removed.
     */

    const maybeSelectStoredReader = async () => {
      if (!locationId) return;

      if (!discoverReaders) {
        console.warn('discoverReaders is not defined. This useEffect should only be called for v2 terminals');
        return;
      }

      const storedReader = PaymentsTerminalController.getStoredReader(locationId!);
      /* by checking for the selectedReader, we are automatically redirecting the user to the 
      collection flow only for the first time until the modal is closed and opened again
      */

      const readersToCheck = await discoverReaders();

      if (storedReader && !selectedReader) {
        const storedReaderIsOnline = readersToCheck.find(
          (reader) => reader.id === storedReader.readerId && reader.status === 'online'
        );

        if (!storedReaderIsOnline) return;

        const storedReaderIsInUse = storedReaderIsOnline?.action?.status === 'in_progress';

        if (storedReaderIsInUse) return;

        onSelectReader(storedReader);
      }
    };

    maybeSelectStoredReader();
  }, [
    locationId,
    selectedReader?.readerId,
    !!discoverReaders /* basically using as on mount. We don't want lots of reactivity*/,
  ]);

  return {
    modalProps,
    triggerProps,
    showLocationChip,
    locationName,
    handleReaderSelect,
    onConfirm,
  };
};
